import { makeObservable, computed, override } from 'mobx'
import { task } from 'mobx-task'
import Job, { PayoutSplit } from 'jobs/Job'
import ModalViewModel from 'infra/ModalViewModel'
import { FindMembers } from 'iam/MemberStore'
import { PayoutSplitAllocatorViewModel } from 'jobs/views/PayoutSplitAllocator/PayoutSplitAllocatorViewModel'
import { Id } from 'types/definitions'
import JobStore, { AllocationInput } from 'jobs/JobStore'
import { AlertFactory, AlertState } from '../../../../../alerts/Alert'

/**
 * Dialog for defining the payout split.
 */
export class SplitPayoutDialogViewModel extends ModalViewModel {
  /**
   * View model for split allocations.
   */
  readonly allocatorVM = new PayoutSplitAllocatorViewModel(
    Id.assert(this.job.workspace.id),
    () => this.job.providerCutAmount,
    this.findMembers,
  )

  /**
   * Constructor.
   */
  constructor(
    private readonly job: Job,
    private readonly findMembers: FindMembers,
    private readonly jobStore: JobStore,
    private readonly newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Shows the modal.
   */
  @override
  show() {
    this.initialize().then()
    super.show()
  }

  /**
   * The job title.
   */
  @computed
  get jobTitle() {
    return this.job.displayName
  }

  /**
   * The existing split.
   */
  @computed
  get existingSplit() {
    return this.job.payoutSplit || PayoutSplit.empty()
  }

  /**
   * Initializes the view model by retrieving necessary info.
   */
  initialize = task(async () => {
    const existingSplit = this.existingSplit
    this.allocatorVM.reset()

    const allocations: AllocationInput[] = existingSplit.allocations.map(
      (allocation) => ({
        user: allocation.user,
        allocationAmount: allocation.amount,
        authorizedPartialPayoutAmount: allocation.authorizedPartialPayoutAmount,
      }),
    )
    this.allocatorVM.setAllocations(allocations)
  })

  /**
   * Split the payout according to the defined allocations.
   */
  splitPayout = task.resolved(async () => {
    if (!this.allocatorVM.validate()) {
      return
    }

    const allocations = this.allocatorVM.getAllocations()
    await this.jobStore.splitPayout(this.job, allocations)

    this.close()
    this.newAlert()
      .saying('Payout saved!', 'The new payout split was saved successfully.')
      .withState(AlertState.Success)
      .autoDismiss()
      .show()
  })
}
